import { useCallback } from 'react';

import { useSearchParams } from 'react-router-dom';
import { todayWallClockDate, toWallClockDate } from '@/utils/wallClock';

/**
 * URL-synced session state for the fresh-straw transfer module. Replaces the
 * ad-hoc param plumbing in FreshStrawContext / FreshStrawSelector. Param keys
 * match what the existing components already read so this hook is a drop-in
 * replacement during the Phase 2 transition.
 *
 * Pattern copied from `src/pages/in-clinic/hooks/useImplantSession.ts`.
 */
export interface FreshStrawSession {
  fertDate: string;
  fertSite: string;
  transferDate: string;
  transferSite: string;
  accountId: string;
  locationId: string;
  freshShipmentId: string;
  loadDate: string;
  recipType: string;
  /** Service-scheduler trip id when the module is launched from a trip stop. */
  tripId: string;
  /** Service-scheduler stop id that seeded transferSite / transferDate. */
  stopId: string;
  /** Which embryo-source tab is currently active. */
  activeTab: string;

  setFertDate: (value: string) => void;
  setFertSite: (value: string) => void;
  setTransferDate: (value: string) => void;
  setTransferSite: (value: string) => void;
  setAccountId: (value: string) => void;
  setLocationId: (value: string) => void;
  setFreshShipmentId: (value: string) => void;
  setLoadDate: (value: string) => void;
  setRecipType: (value: string) => void;
  setTripId: (value: string) => void;
  setStopId: (value: string) => void;
  setActiveTab: (value: string) => void;

  /** True when a trip id + stop id are present in the URL. */
  isTripScoped: boolean;
}

function todayIso(): string {
  return todayWallClockDate();
}

function todayMinusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toWallClockDate(d);
}

export function useFreshStrawSession(): FreshStrawSession {
  const [searchParams, setSearchParams] = useSearchParams();

  const fertDate = searchParams.get('fertDate') || todayMinusDaysIso(7);
  const fertSite = searchParams.get('fertSite') || '';
  const transferDate = searchParams.get('transferDate') || todayIso();
  const transferSite = searchParams.get('transferSite') || '';
  const accountId = searchParams.get('account') || '';
  const locationId = searchParams.get('location') || '';
  const freshShipmentId = searchParams.get('freshShipment') || '';
  const loadDate = searchParams.get('loadDate') || '';
  const recipType = searchParams.get('recipType') || '';
  const tripId = searchParams.get('tripId') || '';
  const stopId = searchParams.get('stopId') || '';
  const activeTab = searchParams.get('tab') || '';

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return {
    fertDate,
    fertSite,
    transferDate,
    transferSite,
    accountId,
    locationId,
    freshShipmentId,
    loadDate,
    recipType,
    tripId,
    stopId,
    activeTab,
    isTripScoped: Boolean(tripId && stopId),
    setFertDate: (v: string) => updateParam('fertDate', v),
    setFertSite: (v: string) => updateParam('fertSite', v),
    setTransferDate: (v: string) => updateParam('transferDate', v),
    setTransferSite: (v: string) => updateParam('transferSite', v),
    setAccountId: (v: string) => updateParam('account', v),
    setLocationId: (v: string) => updateParam('location', v),
    setFreshShipmentId: (v: string) => updateParam('freshShipment', v),
    setLoadDate: (v: string) => updateParam('loadDate', v),
    setRecipType: (v: string) => updateParam('recipType', v),
    setTripId: (v: string) => updateParam('tripId', v),
    setStopId: (v: string) => updateParam('stopId', v),
    setActiveTab: (v: string) => updateParam('tab', v),
  };
}
